import { supabase } from "@/lib/supabaseClient";

export type UserRole = "admin" | "socio" | "colaborador" | "prospecto";

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Administrador",
  socio: "Socio",
  colaborador: "Colaborador",
  prospecto: "Prospecto",
};

export type AdminStats = {
  totalUsers: number;
  usersByRole: { role: UserRole; count: number }[];
  totalProspects: number;
  prospectsByStatus: { status: string; count: number }[];
  avgCourseProgress: number;
  quizCompletedCount: number;
  activeComplianceRules: number;
  activeApprovedMessages: number;
};

/**
 * Trae los números reales del equipo dentro de la organización del admin
 * actual (todo filtrado por RLS: cada consulta solo puede ver filas de su
 * propia organización). Varias consultas livianas en paralelo en vez de
 * una sola mega-consulta, para mantener cada una simple de leer.
 */
export async function fetchAdminStats(): Promise<AdminStats> {
  const [profilesRes, prospectsRes, courseRes, quizRes, rulesRes, messagesRes] = await Promise.all([
    supabase.from("profiles").select("role"),
    supabase.from("prospects").select("status"),
    supabase.from("course_progress").select("percent"),
    supabase.from("quiz_results").select("user_id", { count: "exact", head: true }),
    supabase.from("compliance_rules").select("id", { count: "exact", head: true }).eq("active", true),
    supabase.from("approved_messages").select("id", { count: "exact", head: true }).eq("active", true),
  ]);

  const roles = (profilesRes.data ?? []) as { role: UserRole }[];
  const usersByRoleMap = new Map<UserRole, number>();
  roles.forEach((r) => usersByRoleMap.set(r.role, (usersByRoleMap.get(r.role) ?? 0) + 1));

  const statuses = (prospectsRes.data ?? []) as { status: string }[];
  const statusMap = new Map<string, number>();
  statuses.forEach((p) => statusMap.set(p.status, (statusMap.get(p.status) ?? 0) + 1));

  const percents = ((courseRes.data ?? []) as { percent: number }[]).map((r) => r.percent);
  const avgCourseProgress = percents.length
    ? Math.round(percents.reduce((sum, p) => sum + p, 0) / percents.length)
    : 0;

  return {
    totalUsers: roles.length,
    usersByRole: Array.from(usersByRoleMap.entries()).map(([role, count]) => ({ role, count })),
    totalProspects: statuses.length,
    prospectsByStatus: Array.from(statusMap.entries()).map(([status, count]) => ({ status, count })),
    avgCourseProgress,
    quizCompletedCount: quizRes.count ?? 0,
    activeComplianceRules: rulesRes.count ?? 0,
    activeApprovedMessages: messagesRes.count ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Usuarios
// ---------------------------------------------------------------------------

export type AdminUser = {
  id: string;
  fullName: string | null;
  role: UserRole;
  createdAt: string;
  sponsorId: string | null;
  sponsorName: string | null;
};

export async function fetchOrgUsers(): Promise<AdminUser[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, role, created_at, sponsor_id")
    .order("created_at", { ascending: false });
  if (error) throw error;

  const rows = data ?? [];
  const nameById = new Map(rows.map((r) => [r.id as string, r.full_name as string | null]));

  return rows.map((r) => ({
    id: r.id,
    fullName: r.full_name,
    role: r.role,
    createdAt: r.created_at,
    sponsorId: r.sponsor_id,
    sponsorName: r.sponsor_id ? nameById.get(r.sponsor_id) ?? null : null,
  }));
}

export async function updateUserRole(userId: string, role: UserRole): Promise<void> {
  const { error } = await supabase.from("profiles").update({ role }).eq("id", userId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Reglas de cumplimiento
// ---------------------------------------------------------------------------

export type ComplianceSeverity = "info" | "warning" | "critical";

export type ComplianceRule = {
  id: string;
  title: string;
  description: string;
  severity: ComplianceSeverity;
  active: boolean;
  createdAt: string;
};

function mapRule(r: Record<string, unknown>): ComplianceRule {
  return {
    id: r.id as string,
    title: r.title as string,
    description: r.description as string,
    severity: r.severity as ComplianceSeverity,
    active: r.active as boolean,
    createdAt: r.created_at as string,
  };
}

export async function fetchComplianceRules(): Promise<ComplianceRule[]> {
  const { data, error } = await supabase
    .from("compliance_rules")
    .select("id, title, description, severity, active, created_at")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []).map(mapRule);
}

export async function createComplianceRule(input: {
  title: string;
  description: string;
  severity: ComplianceSeverity;
}): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const { data: profile } = await supabase
    .from("profiles")
    .select("organization_id")
    .eq("id", userData.user?.id ?? "")
    .maybeSingle();
  const { error } = await supabase.from("compliance_rules").insert({
    organization_id: profile?.organization_id,
    title: input.title,
    description: input.description,
    severity: input.severity,
  });
  if (error) throw error;
}

export async function updateComplianceRule(
  id: string,
  patch: Partial<{ title: string; description: string; severity: ComplianceSeverity; active: boolean }>
): Promise<void> {
  const { error } = await supabase.from("compliance_rules").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteComplianceRule(id: string): Promise<void> {
  const { error } = await supabase.from("compliance_rules").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Palabras y frases controladas + motor de detección (sin IA: comparación de texto)
// ---------------------------------------------------------------------------

export type ComplianceTermCategory = "prohibida" | "restringida" | "observacion";

export type ComplianceTerm = {
  id: string;
  phrase: string;
  category: ComplianceTermCategory;
  active: boolean;
};

export type ComplianceTermMatch = {
  termId: string;
  phrase: string;
  category: ComplianceTermCategory;
  matches: number;
};

export async function fetchComplianceTerms(): Promise<ComplianceTerm[]> {
  const { data, error } = await supabase
    .from("compliance_terms")
    .select("id, phrase, category, active")
    .order("phrase", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ComplianceTerm[];
}

export async function createComplianceTerm(input: {
  phrase: string;
  category: ComplianceTermCategory;
}): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const { data: profile } = await supabase
    .from("profiles")
    .select("organization_id")
    .eq("id", userData.user?.id ?? "")
    .maybeSingle();
  const { error } = await supabase.from("compliance_terms").insert({
    organization_id: profile?.organization_id,
    phrase: input.phrase,
    category: input.category,
  });
  if (error) throw error;
}

export async function deleteComplianceTerm(id: string): Promise<void> {
  const { error } = await supabase.from("compliance_terms").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Motor de detección (sin IA): cuenta coincidencias reales de cada término
 * controlado dentro del contenido que ya existe en Supabase (mensajes
 * aprobados + aperturas sugeridas a prospectos), vía la función SQL
 * compliance_term_matches (comparación de texto simple, ILIKE).
 */
export async function fetchComplianceTermMatches(): Promise<ComplianceTermMatch[]> {
  const { data: userData } = await supabase.auth.getUser();
  const { data: profile } = await supabase
    .from("profiles")
    .select("organization_id")
    .eq("id", userData.user?.id ?? "")
    .maybeSingle();
  if (!profile?.organization_id) return [];

  const { data, error } = await supabase.rpc("compliance_term_matches", {
    p_organization_id: profile.organization_id,
  });
  if (error) throw error;

  return ((data ?? []) as Array<{ term_id: string; phrase: string; category: ComplianceTermCategory; matches: number }>).map(
    (r) => ({ termId: r.term_id, phrase: r.phrase, category: r.category, matches: Number(r.matches) })
  );
}

// ---------------------------------------------------------------------------
// Biblioteca de mensajes aprobados
// ---------------------------------------------------------------------------

export type ApprovedMessage = {
  id: string;
  title: string;
  category: string;
  content: string;
  active: boolean;
  createdAt: string;
};

export async function fetchApprovedMessages(): Promise<ApprovedMessage[]> {
  const { data, error } = await supabase
    .from("approved_messages")
    .select("id, title, category, content, active, created_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((m) => ({
    id: m.id,
    title: m.title,
    category: m.category,
    content: m.content,
    active: m.active,
    createdAt: m.created_at,
  }));
}

export async function createApprovedMessage(input: { title: string; category: string; content: string }): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const { data: profile } = await supabase
    .from("profiles")
    .select("organization_id")
    .eq("id", userData.user?.id ?? "")
    .maybeSingle();
  const { error } = await supabase.from("approved_messages").insert({
    organization_id: profile?.organization_id,
    title: input.title,
    category: input.category,
    content: input.content,
  });
  if (error) throw error;
}

export async function updateApprovedMessage(
  id: string,
  patch: Partial<{ title: string; category: string; content: string; active: boolean }>
): Promise<void> {
  const { error } = await supabase.from("approved_messages").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteApprovedMessage(id: string): Promise<void> {
  const { error } = await supabase.from("approved_messages").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Bitácora de administración (se llena sola vía triggers — nunca se inserta desde el cliente)
// ---------------------------------------------------------------------------

export type AuditLogEntry = {
  id: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  entity: string;
  summary: string;
  createdAt: string;
};

export async function fetchAuditLog(limit = 25): Promise<AuditLogEntry[]> {
  const { data, error } = await supabase
    .from("admin_audit_log")
    .select("id, actor_id, action, entity, summary, created_at")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;

  const rows = data ?? [];
  const actorIds = Array.from(new Set(rows.map((r) => r.actor_id).filter(Boolean))) as string[];
  let nameById = new Map<string, string | null>();
  if (actorIds.length) {
    const { data: actors } = await supabase.from("profiles").select("id, full_name").in("id", actorIds);
    nameById = new Map((actors ?? []).map((a) => [a.id as string, a.full_name as string | null]));
  }

  return rows.map((r) => ({
    id: r.id,
    actorId: r.actor_id,
    actorName: r.actor_id ? nameById.get(r.actor_id) ?? null : null,
    action: r.action,
    entity: r.entity,
    summary: r.summary,
    createdAt: r.created_at,
  }));
}
